/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import axios, { Method, AxiosResponse } from "axios";

import store from "../store";
import config from "../config";
import { warn } from "./messages";
import Package from "../../package.json";
import { intl } from "../containers/app";
import { processSignIn, signOut } from "./session";
import { getAccessToken } from "./redux/session/reducer";
import { setShouldReload } from "./redux/reload/actions";
import { czechLocale } from "../components/app/languages";

export const CLIENT_PLACEHOLDER = "{clientId}";

/**
 * třída pro uložení aktuálního ID klienta
 */
class HttpData {
  /**
   * ID klienta
   */
  static clientId: string;
  /**
   * zvoleny jazyk
   */
  static language: string;
}

/**
 * při změně redux store nastaví aktuálního klienta
 */
function listener(): void {
  const { data } = store.getState().client;
  HttpData.clientId = data && data.id;
  const language = store.getState().language;
  HttpData.language = language.data && language.data.localization;
}

store.subscribe(listener);

/**
 * třída pro jednorázové použití tokenu bez přihlášení
 */
export class Token {
  /**
   * token
   */
  static value: string;
}

/**
 * přednastavená instance axios
 */
const instance = axios.create({
  baseURL: config.BASE_URL,
  timeout: config.TIMEOUT,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

instance.interceptors.response.use(
  (response) => {
    response &&
      response.headers &&
      response.headers["app-update"] &&
      store.dispatch(setShouldReload(true));
    return response;
  },
  async (error) => {
    if (error && error.response) {
      const originalRequest = error.config;
      if (error.response.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await instance.post("/auth/refresh");
          const { data } = response;
          const token = data.data.token;
          if (token) {
            await processSignIn(response);
            processQueue(null, data.data.token);

            originalRequest.headers.Authorization = `Bearer ${data.data.token}`;
            return instance(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError);
        } finally {
          isRefreshing = false;
        }
        setTimeout(
          () =>
            warn(
              intl.formatMessage({
                id: "session.securityLogout",
                defaultMessage: "Došlo k bezpečnostnímu odhlášení!",
              }),
            ),
          3000,
        );
        signOut(false);
      } else if (error.response.status === 403) {
        warn(
          intl.formatMessage({
            id: "session.authorizationError",
            defaultMessage: "K této operaci nemáte práva!",
          }),
        );
      }
    }

    return Promise.reject(error);
  },
);

/**
 * zkontroluje HTTP status, jestli je 200 až 304
 * @param {number} response HTTP status
 */
export const checkHttpStatus = (response: AxiosResponse): boolean =>
  response ? response.status >= 200 && response.status <= 304 : false;

/**
 * zkontroluje odpověď API při PUT, POST, DELETE požadavku
 * @param response odpověď API
 */
export const checkResponse = (response: AxiosResponse): boolean =>
  response.data && response.data.success;

/**
 * zkontroluje odpověď API při GET požadavku
 * @param response odpověď API
 */
export const checkResponseGet = (response: AxiosResponse): boolean =>
  response.data &&
  (response.data.hardSuccess || (response.data.success && response.data.data));

/**
 * zkontroluje odpověď API při GET požadavku na více objektů
 * @param response odpověď API
 */
export const checkResponseGetMany = (response: AxiosResponse): boolean =>
  response.data && response.data.success && response.data.values;

/**
 * vytvoří základní hlavičky
 */
const getHeaders = (isFormData = false): any => ({
  Accept: "application/json",
  "wa-version": Package.version,
  "Content-Type": isFormData ? "multipart/form-data" : "application/json",
  "Accept-Language": HttpData.language || czechLocale,
});

/**
 * vytvoří objekt hlavičky
 * @param {string} token načtený JWT
 */
const getBearerHeaders = async (
  token?: string,
  isFormData = false,
): Promise<any | undefined> => {
  if (token === undefined) {
    try {
      token = getAccessToken();
    } catch (err) {
    } finally {
      if (!token && Token.value) {
        token = Token.value;
      }
    }
  }
  return {
    ...getHeaders(isFormData),
    "wa-client-id": HttpData.clientId,
    Authorization: "Bearer " + token,
  };
};

/**
 * zašle HTTP požadavek
 * @param {Method} method http metoda
 * @param {string} endpoint endpoint cílového serveru
 * @param {any} data data k odeslaní
 * @param {boolean} auth použití autentizace
 * @param {boolean} json serializace dat do JSON
 * @param {any} onUploadProgress metoda, která vrací informace o průběhu uploadu dat
 * @param {number} timeout timeout pozadavku [ms]
 * @param {boolean} isBlob jestli je vysledek pozadavku blob
 */
async function sendRequest(
  method: Method,
  endpoint: string,
  data: any,
  auth: boolean,
  json = true,
  onUploadProgress: any = undefined,
  timeout: number = config.TIMEOUT,
  isBlob = false,
  isFormData = false,
): Promise<AxiosResponse<any, any>> {
  return await instance.request({
    method,
    timeout,
    url: endpoint,
    onUploadProgress,
    data: json ? JSON.stringify(data) : data,
    headers: auth
      ? await getBearerHeaders(undefined, isFormData)
      : getHeaders(isFormData),
    responseType: isBlob ? "blob" : undefined,
  });
}

/**
 * zašle HTTP PUT požadavek
 * @param {string} endpoint endpoint cílového serveru
 * @param {any} data data k odeslaní
 * @param {boolean} auth jestli se ma použit token
 */
export async function put(
  endpoint: string,
  data: Record<string, unknown>,
  auth = true,
): Promise<AxiosResponse<any, any>> {
  if (endpoint.indexOf(CLIENT_PLACEHOLDER) !== -1) {
    endpoint = endpoint.replace(CLIENT_PLACEHOLDER, HttpData.clientId);
  }
  return await sendRequest("put", endpoint, data, auth);
}

/**
 * zašle HTTP POST požadavek
 * @param {string} endpoint endpoint cílového serveru
 * @param {any} data data k odeslaní
 * @param {boolean} auth jestli se ma použit token
 * @param {number} timeout timeout pozadavku [ms]
 * @param {boolean} isBlob jestli je vysledek pozadavku blob
 * @param {any} onUploadProgress metoda, která vrací informace o průběhu uploadu dat
 * @param {boolean} json serializace dat do JSON
 */
export async function post(
  endpoint: string,
  data: Record<string, unknown>,
  auth: boolean = true,
  timeout: number = config.TIMEOUT,
  isBlob: boolean = false,
  onUploadProgress: any = undefined,
  json: boolean = true,
): Promise<AxiosResponse<any, any>> {
  return await sendRequest(
    "post",
    endpoint,
    data,
    auth,
    json,
    onUploadProgress,
    timeout,
    isBlob,
  );
}

/**
 * zašle HTTP PATCH požadavek
 * @param {string} endpoint endpoint cílového serveru
 * @param {any} data data k odeslaní
 * @param {boolean} auth jestli se ma použit token
 */
export async function patch(
  endpoint: string,
  data: Record<string, unknown>,
  auth: boolean = true,
): Promise<AxiosResponse<any, any>> {
  return await sendRequest("patch", endpoint, data, auth);
}

/**
 * zašle HTTP POST požadavek, ale nechá defaultní formát dat - neprovede serializaci na JSON
 * @param {string} endpoint endpoint cílového serveru
 * @param {any} data data k odeslaní
 * @param {boolean} auth jestli se ma použit token
 * @param {any} onUploadProgress metoda, která vrací informace o průběhu uploadu dat
 * @param {number} timeout timeout pozadavku [ms]
 */
export async function postForm(
  endpoint: string,
  data: Record<string, unknown>,
  auth = true,
  onUploadProgress: any = undefined,
  timeout: number = config.TIMEOUT,
): Promise<AxiosResponse<any, any>> {
  return await sendRequest(
    "post",
    endpoint,
    data,
    auth,
    false,
    onUploadProgress,
    timeout,
    false,
    true,
  );
}

/**
 * zašle HTTP GET požadavek
 * @param {string} endpoint endpoint cílového serveru
 * @param {boolean} auth jestli se ma použit token
 * @param {number} timeout timeout pozadavku [ms]
 */
export async function get(
  endpoint: string,
  auth = true,
  timeout: number = config.TIMEOUT,
  isBlob = false,
): Promise<AxiosResponse<any, any>> {
  if (endpoint.indexOf(CLIENT_PLACEHOLDER) !== -1) {
    endpoint = endpoint.replace(CLIENT_PLACEHOLDER, HttpData.clientId);
  }

  return await sendRequest(
    "get",
    endpoint,
    undefined,
    auth,
    false,
    undefined,
    timeout,
    isBlob,
  );
}

/**
 * zašle HTTP DELETE požadavek
 * @param {string} endpoint endpoint cílového serveru
 * @param {boolean} auth jestli se ma použit token
 */
export async function deleteApi(
  endpoint: string,
  auth = true,
): Promise<AxiosResponse<any, any>> {
  return await sendRequest("delete", endpoint, undefined, auth);
}
